import { atom, DefaultValue, selector, selectorFamily } from "recoil";
import { RaFillingAccordionValidation } from "./ra-filling/config";

export const createRAState = atom({
    key: 'createRA',
    default: {
        step: {
            active: -1,
            completed: [true, false, false, false, false, false],
            numSteps: 7
        },
        preauth: { },
        bookingData: {
            value: {}
        },
        createRaPricingInfo: {
            value: null,
            error: false
        },
        prefixesList: [],
        driverResidenceCountriesList: [],
        driverResidenceProvincesList: {
            mainDriver: [],
            additionalDrivers: [[],[]]
        },
        recoveryRaInfo: {
            recoveryModeFlag: false,
            value: null
        },
        customerData: {
            accordions: {
                mainDriver: true,
                addDrivers: []
            },
            value: {}
        },
        checkDriverValidity: {
            value: {}
        },
        otpValidation: {
            value: {
                mainDriver: {
                    email: undefined,
                    mobile: undefined
                },
                additionalDrivers: [
                    {
                        email: undefined,
                        mobile: undefined
                    },
                    {
                        email: undefined,
                        mobile: undefined
                    }
                ]
            }
        },
        raFilling: {
            value: {
                vehicleDetails: {},
                financialInfo: {},
                billingOwnerInfo: {},
                ancillariesInfo: {},
                quotationInfo: {}
            }
        },
        listVehiclesReady: {
            value: null
        },
        counterSaleManagement: {
            value: {previous: null, last: null}
        },
        verifySign: {
            value: {
                digitalSignature: false,
                paperSignature: false,
                signed: false
            }
        },
        raKeylessInfo: {
            value: "",
        },
    }
});

export const stepState = selector<any>({
    key: 'step',
    get: ({ get }) => get(createRAState).step,
    set: ({ set, get }, newValue) => {
        set(createRAState, {
            ...get(createRAState),
            step: newValue instanceof DefaultValue ? get(createRAState).step : newValue
        })
    }
});

export const preauthState = selector<any>({
    key: 'preauth',
    get: ({ get }) => get(createRAState).preauth,
    set: ({ set, get }, newValue) => {
        set(createRAState, {
            ...get(createRAState),
            preauth: newValue
        })
    }
});

export const prefixesListState = selector<any>({
    key: 'prefixesList',
    get: ({ get }) => get(createRAState).prefixesList,
    set: ({ set, get }, newValue) => {
        set(createRAState, {
            ...get(createRAState),
            prefixesList: newValue instanceof DefaultValue ? get(createRAState).prefixesList : newValue
        })
    }
});

export const driverResidenceCountriesListState = selector<any>({
    key: 'driverResidenceCountriesList',
    get: ({ get }) => get(createRAState).driverResidenceCountriesList,
    set: ({ set, get }, newValue) => {
        set(createRAState, {
            ...get(createRAState),
            driverResidenceCountriesList: newValue instanceof DefaultValue ? get(createRAState).driverResidenceCountriesList : newValue
        })
    }
});

export const allDriverResidenceProvincesListState = selector<any>({
    key: 'allDriverResidenceProvincesList',
    get: ({ get }) => get(createRAState).driverResidenceProvincesList,
    set: ({ set, get }, newValue) => {
        set(createRAState, {
            ...get(createRAState),
            driverResidenceProvincesList: newValue instanceof DefaultValue ? get(createRAState).driverResidenceProvincesList : newValue
        })
    }
});

export const driverResidenceProvincesListState = selectorFamily<boolean, string>({
    key: 'driverResidenceProvincesList',
    get: driver => ({ get }) => get(allDriverResidenceProvincesListState)[driver],
    set: driver => ({ get, set }, newValue) => {
        set(allDriverResidenceProvincesListState, {
            ...get(allDriverResidenceProvincesListState),
            [driver]: newValue instanceof DefaultValue ? false : newValue
        })
    }
});

export const customerDataAccordionsState = selector<any>({
    key: 'customerDataAccordions',
    get: ({ get }) => get(createRAState).customerData.accordions,
    set: ({ set, get }, newValue) => {
        set(createRAState, {
            ...get(createRAState),
            customerData: {
                ...get(createRAState).customerData,
                accordions: newValue instanceof DefaultValue ? get(createRAState).customerData.accordions : newValue
            }
        })
    }
})

export const recoveryRaInfoState = selector<any>({
    key: 'recoveryRaInfo',
    get: ({ get }) => get(createRAState).recoveryRaInfo.value,
    set: ({ set, get }, newValue) => {
        set(createRAState, {
            ...get(createRAState),
            recoveryRaInfo: {
                ...get(createRAState).recoveryRaInfo,
                value: newValue instanceof DefaultValue ? get(createRAState).recoveryRaInfo.value : newValue
            }
        })
    }
})

export const recoveryRaModeState = selector<any>({
    key: 'recoveryRaModeState',
    get: ({ get }) => get(createRAState).recoveryRaInfo.recoveryModeFlag,
    set: ({ set, get }, newValue) => {
        set(createRAState, {
            ...get(createRAState),
            recoveryRaInfo: {
                ...get(createRAState).recoveryRaInfo,
                recoveryModeFlag: newValue instanceof DefaultValue ? get(createRAState).recoveryRaInfo.recoveryModeFlag : newValue
            }
        })
    }
})

export const customerDataAccordionState = selectorFamily<boolean, string>({
    key: 'customerDataAccordion',
    get: accordionName => ({ get }) => get(customerDataAccordionsState)[accordionName],
    set: accordionName => ({ get, set }, newValue) => {
        set(customerDataAccordionsState, {
            ...get(customerDataAccordionsState),
            [accordionName]: newValue instanceof DefaultValue ? false : newValue
        })
    }
});

export const bookingDataValueState = selector<any>({
    key: 'bookingDataValue',
    get: ({get}) => get(createRAState).bookingData.value,
    set: ({get, set}, newValue) => {
        set(createRAState, {
            ...get(createRAState),
            bookingData: {
                ...get(createRAState).bookingData,
                value: newValue instanceof DefaultValue ? get(createRAState).bookingData.value : newValue
            }
        })
    }
});

export const bookingDataValueFamilyState = selectorFamily({
    key: 'bookingDataValueFamily',
    // @ts-ignore
    get: (subValue) => ({get}) => get(createRAState).bookingData.value[subValue]
});

export const customerDataValueState = selector<any>({
    key: 'customerDetailsValue',
    get: ({get}) => get(createRAState).customerData.value,
    set: ({get, set}, newValue) => {
        set(createRAState, {
            ...get(createRAState),
            customerData: {
                ...get(createRAState).customerData,
                value: newValue instanceof DefaultValue ? get(createRAState).customerData.value : newValue
            }
        })
    }
});

export const checkDriverValidityValueState = selector<any>({
    key: 'checkDriverValidityValue',
    get: ({get}) => get(createRAState).checkDriverValidity.value,
    set: ({get, set}, newValue) => {
        set(createRAState, {
            ...get(createRAState),
            checkDriverValidity: {
                ...get(createRAState).checkDriverValidity,
                value: newValue instanceof DefaultValue ? get(createRAState).checkDriverValidity.value : newValue
            }
        })
    }
});

export const rentalAgreementFillingValueState = selector<any>({
    key: 'rentalAgreementFillingValue',
    get: ({get}) => get(createRAState).raFilling.value,
    set: ({get, set}, newValue) => {
        set(createRAState, {
            ...get(createRAState),
            raFilling: {
                ...get(createRAState).raFilling,
                value: newValue instanceof DefaultValue ? get(createRAState).raFilling.value : newValue
            }
        })
    }
});

export const otpValidationValueState = selector<any>({
    key: 'otpValidationValueState',
    get: ({get}) => get(createRAState).otpValidation.value,
    set: ({get, set}, newValue) => {
        set(createRAState, {
            ...get(createRAState),
            otpValidation: {
                ...get(createRAState).otpValidation,
                value: newValue instanceof DefaultValue ? get(createRAState).otpValidation.value : newValue
            }
        })
    }
});

export const verifySignState = selector<any>({
    key: 'verifySignState',
    get: ({get}) => get(createRAState).verifySign.value,
    set: ({get, set}, newValue) => {
        set(createRAState, {
            ...get(createRAState),
            verifySign: {
                ...get(createRAState).verifySign,
                value: newValue instanceof DefaultValue ? get(createRAState).verifySign.value : newValue
            }
        })
    }
});

export const counterSaleManagementValueState = selector<any>({
    key: 'counterSaleManagementValueState',
    get: ({get}) => get(createRAState).counterSaleManagement.value,
    set: ({get, set}, newValue) => {
        set(createRAState, {
            ...get(createRAState),
            counterSaleManagement: {
                ...get(createRAState).counterSaleManagement,
                value: newValue instanceof DefaultValue ? get(createRAState).counterSaleManagement.value : newValue
            }
        })
    }
});

export const createRaPricingInfoState = selector<any>({
    key: 'createRaPricingInfoState',
    get: ({get}) => get(createRAState).createRaPricingInfo.value,
    set: ({get, set}, newValue) => {
        set(createRAState, {
            ...get(createRAState),
            createRaPricingInfo: {
                ...get(createRAState).createRaPricingInfo,
                value: newValue instanceof DefaultValue ? get(createRAState).createRaPricingInfo.value : newValue
            }
        })
    }
});

export const createRaPricingInfoErrorState = selector<any>({
    key: 'createRaPricingInfoErrorState',
    get: ({get}) => get(createRAState).createRaPricingInfo.error,
    set: ({get, set}, newValue) => {
        set(createRAState, {
            ...get(createRAState),
            createRaPricingInfo: {
                ...get(createRAState).createRaPricingInfo,
                error: newValue instanceof DefaultValue ? get(createRAState).createRaPricingInfo.error : newValue
            }
        })
    }
});

export const listVehiclesReadyState = selector<any>({
    key: 'listVehiclesReadyState',
    get: ({get}) => get(createRAState).listVehiclesReady.value,
    set: ({get, set}, newValue) => {
        set(createRAState, {
            ...get(createRAState),
            listVehiclesReady: {
                ...get(createRAState).listVehiclesReady,
                value: newValue instanceof DefaultValue ? get(createRAState).listVehiclesReady.value : newValue
            }
        })
    }
});

export const raFillingAccordionValidationState = atom<RaFillingAccordionValidation>({
    key: 'raFillingAccordionValidationState',
    default: {
        generalInfo: true,
        firstBillingOwnerInfo: false,
        secondBillingOwnerInfo: false,
        ancillariesInfo: false
    }
})

export const createRATimezoneState = atom<string>({
    key: 'createRATimezoneState',
    default: "Europe/Rome"
})

export const raKeylessInfoState = selector<any>({
    key: 'raKeylessInfo',
    get: ({get}) => get(createRAState).raKeylessInfo.value,
    set: ({get, set}, newValue) => {
        set(createRAState, {
            ...get(createRAState),
            raKeylessInfo: {
                ...get(createRAState).raKeylessInfo,
                value: newValue
            }
        })
    }
})
